import { useHoverPopover } from '@app/core/hooks';
import { Box, Popover, PopoverOrigin, Typography } from '@mui/material';
import { createContext, ReactNode, useContext } from 'react';

/** Context so descendants can close the popover (e.g. on button click). */
const HoverPopoverCloseContext = createContext<(() => void) | null>(null);

/** Hook for content inside HoverPopover to close it programmatically. */
export function useHoverPopoverClose(): (() => void) | null {
  return useContext(HoverPopoverCloseContext);
}

export interface HoverPopoverProps {
  /** Element that opens the popover on hover. Wrapped in a span so mouse handlers can be attached. */
  trigger: ReactNode;
  /** Title shown above the popover content. Rendered as subtitle2 Typography. */
  title?: ReactNode;
  /** Content rendered inside the popover, below the title. */
  children: ReactNode;
  /**
   * Delay in ms before closing when mouse leaves the trigger.
   * Allows moving the cursor from trigger to popover.
   * @default 150
   */
  delay?: number;
  /** Whether the popover is enabled. When false, it never opens. */
  enabled?: boolean;
  /**
   * How to position the popover.
   * - 'element': anchor to the trigger (default).
   * - 'cursor': position at the cursor when opening.
   * - 'cursor-x-element-y': X from cursor, Y from trigger top; popup follows cursor horizontally but stays n px above the trigger.
   * @default 'element'
   */
  positionMode?: 'element' | 'cursor' | 'cursor-x-element-y';
  /**
   * Offset in px from the cursor when positionMode is 'cursor'.
   * When positionMode is 'cursor-x-element-y', top is offset from trigger top (e.g. -8 = 8px above pill), left is offset from cursor X.
   * E.g. { top: -16 } places the anchor above the cursor (cursor mode) or 16px above trigger top (cursor-x-element-y).
   */
  cursorOffset?: { top?: number; left?: number };
  /** Popover anchor position relative to the trigger (element mode) or the cursor point (cursor mode). */
  anchorOrigin?: PopoverOrigin;
  /** Position from which the popover grows. */
  transformOrigin?: PopoverOrigin;
  /** Extra sx for the popover content Box. */
  contentSx?: Record<string, unknown>;
  /** Extra sx for the popover Paper. */
  paperSx?: Record<string, unknown>;
}

/** Default anchor/transform for cursor mode: popover bottom-center at the anchor point (above cursor). */
const CURSOR_POSITION_DEFAULTS = {
  anchorOrigin: { vertical: 'bottom' as const, horizontal: 'center' as const },
  transformOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'center' as const,
  },
};

/**
 * Reusable hover popover: shows content when the user hovers over the trigger.
 * Uses pointer-events so the user can move the cursor from the trigger to the
 * popover without it closing. Uses useHoverPopover for open/close timing.
 */
export function HoverPopover({
  trigger,
  title,
  children,
  delay = 150,
  enabled = true,
  positionMode = 'element',
  cursorOffset,
  anchorOrigin = positionMode === 'cursor' || positionMode === 'cursor-x-element-y'
    ? CURSOR_POSITION_DEFAULTS.anchorOrigin
    : { vertical: 'top', horizontal: 'right' },
  transformOrigin = positionMode === 'cursor' || positionMode === 'cursor-x-element-y'
    ? CURSOR_POSITION_DEFAULTS.transformOrigin
    : { vertical: 'bottom', horizontal: 'right' },
  contentSx,
  paperSx,
}: HoverPopoverProps) {
  const {
    anchorEl,
    anchorPosition,
    anchorReference,
    menuOpen,
    handleMouseEnter,
    handleMouseLeave,
    handlePopoverMouseEnter,
    handlePopoverMouseLeave,
    closePopover,
  } = useHoverPopover({ enabled, delay, positionMode, cursorOffset });

  return (
    <>
      <Box
        component="span"
        sx={{ display: 'inline-flex' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {trigger}
      </Box>
      <Popover
        open={menuOpen}
        anchorEl={anchorReference === 'anchorEl' ? anchorEl : undefined}
        anchorReference={anchorReference}
        anchorPosition={anchorReference === 'anchorPosition' ? anchorPosition : undefined}
        sx={{ pointerEvents: 'none' }}
        slotProps={{
          paper: {
            onMouseEnter: handlePopoverMouseEnter,
            onMouseLeave: handlePopoverMouseLeave,
            sx: { pointerEvents: 'auto', ...paperSx },
          },
        }}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        disableRestoreFocus
      >
        <HoverPopoverCloseContext.Provider value={closePopover}>
          <Box sx={{ p: 1.5, maxWidth: 280, ...contentSx }}>
            {title && (
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                {title}
              </Typography>
            )}
            {children}
          </Box>
        </HoverPopoverCloseContext.Provider>
      </Popover>
    </>
  );
}
